import React, {useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import Svg, {Path, Rect} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import BackToPrevious from '@/Atoms/BackToPrevious';
import {
  useGetPlayerProfileApi,
  ProfileViewAs,
} from '@/Hooks/PlayerProfileApi/useGetPlayerProfileApi';
import {
  PlayerProfileFull,
  PlayerProfilePrivate,
  PersonalDetails,
} from '@/Hooks/PlayerProfileApi/types';
import {useSetProfilePrivacyApi} from '@/Hooks/PlayerProfileApi/useSetProfilePrivacyApi';
import {useDashboardApiQuery} from '@/Hooks/DashboardApi/dashboardApi';
import ProfileHeader from './components/ProfileHeader';
import PersonalDetailsCard from './components/PersonalDetailsCard';
import AnonymousHeader from './components/AnonymousHeader';
import TeaserNudgeBanner from './components/TeaserNudgeBanner';
import LinkedAccountsRow from './components/LinkedAccountsRow';
import OverviewTab from './components/tabs/OverviewTab';
import GamesTab from './components/tabs/GamesTab';
import TrainingTab from './components/tabs/TrainingTab';
import AssessmentTab from './components/tabs/AssessmentTab';
import ClubsTab from './components/tabs/ClubsTab';
import LockedTabPanel from './components/LockedTabPanel';
import PrivateProfileNotice from './components/PrivateProfileNotice';
import FadeInUp from './components/ui/FadeInUp';

// Back control for the profile screen.
//
// The raw navigation stack (getState().routes) is unreliable as a "where did I
// come from" source here: the left-rail MainMenu navigates with
// CommonActions.reset(), and navigate() reuses existing routes, so the stack
// order doesn't reflect the real journey. The app-wide navHistoryStore —
// recorded off NavigationContainer.onStateChange — is the authoritative
// source instead. It steps back one profile at a time for a profile→profile
// chain (A -> B -> C) too: setRoute (see navHistoryStore.ts) only collapses a
// same-name PlayerProfile transition when the username also matches, so
// visiting a different player's profile always pushes a new, distinct entry
// rather than overwriting the one you're leaving.
function ProfileBackButton({navigation}: {navigation: any}) {
  return <BackToPrevious navigation={navigation} />;
}

// Owner-only "View as" preview indicator -- just a small CTA next to the
// back button, not a loud full-width banner. The picker that starts a
// preview lives in ProfileHeader's kebab menu; this is only the way back out
// of one.
function ExitPreviewButton({onPress}: {onPress: () => void}) {
  return (
    <Pressable testID="view-as-exit" style={styles.exitPreviewPill} onPress={onPress}>
      <CCText style={styles.exitPreviewText}>Exit preview</CCText>
    </Pressable>
  );
}

// Shapes the "Personal details" card's data from the app-wide ['dashboard']
// query (dashboard/v1/summary/), which already serializes the full CCUser
// model under `user` -- no separate profile-endpoint field for this (see
// dashboardApi.ts / ChatTab.tsx's identical "React Query cache — no extra
// request" pattern). `user_timezone` comes back as `{selected, list}` here,
// unlike a plain string.
function buildPersonalDetails(user: any): PersonalDetails | null {
  if (!user) {
    return null;
  }
  return {
    email: user.email || null,
    phone: user.mobile_number || null,
    alternate_phone: user.alternate_mobile_number || null,
    pincode: user.pincode || null,
    timezone: user.user_timezone?.selected || null,
    gender: user.gender || null,
    dob: user.dob || null,
  };
}

// Small outline padlock shown on locked tabs in the anonymous view.
function LockGlyph({color = '#888280'}: {color?: string}) {
  return (
    <Svg width={12} height={12} viewBox="0 0 24 24" fill="none">
      <Rect x={5} y={11} width={14} height={9} rx={2} stroke={color} strokeWidth={2.5} />
      <Path d="M8 11V7a4 4 0 0 1 8 0v4" stroke={color} strokeWidth={2.5} />
    </Svg>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAF6EB',
  },
  scrollContent: {
    // A touch more breathing room between the left nav rail and the cards,
    // matching the design's wider gutter.
    paddingHorizontal: 36,
    paddingVertical: 24,
  },
  // Full-width, edge-to-edge (no centered cap). The hero rating card carries
  // its own proportional width (see ProfileHeader/styles.ts ratingCard) so it
  // stays wide relative to the identity column at any viewport.
  inner: {
    width: '100%',
  },
  centerState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  // Loading skeleton (spec §4 "Loading"): a hero-shaped placeholder card
  // instead of a bare centered spinner.
  skeletonCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 28,
    paddingHorizontal: 30,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  skeletonAvatar: {
    width: 104,
    height: 104,
    borderRadius: 52,
    backgroundColor: '#EFE7D2',
    marginRight: 26,
  },
  skeletonLine: {
    height: 16,
    borderRadius: 8,
    backgroundColor: '#EFE7D2',
  },
  loadingText: {
    fontSize: 13,
    color: '#888280',
    marginTop: 16,
  },
  tabBarRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 16,
  },
  // Design handoff's tab strip is a rounded-rect segmented control
  // (container radius 16, tabs radius 12), not a full-pill row.
  tabStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 5,
    borderWidth: 1,
    borderColor: '#E0DDD9',
    // React Native's flex default is flexShrink:0 (unlike web CSS's 1), so
    // without this the strip never shrinks to the row's actual available
    // width on a narrow viewport -- it just overflows past the card edge
    // instead of wrapping its own tabs onto a second line.
    flexShrink: 1,
    minWidth: 0,
  },
  tabPill: {
    paddingVertical: 11,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  tabPillActive: {
    backgroundColor: '#4D3F37',
  },
  tabPillDisabled: {
    opacity: 0.5,
  },
  tabPillText: {
    fontSize: 13,
    color: '#4D3F37',
    fontWeight: '600',
  },
  tabPillTextActive: {
    color: '#fff',
  },
  tabVisibilityNote: {
    fontSize: 12,
    color: '#888280',
  },
  // Anonymous tab pill: label + lock glyph in a row.
  tabPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  // Faded Overview teaser (anonymous): a dimmed preview of the Results card.
  anonTeaserCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingVertical: 20,
    paddingHorizontal: 22,
    marginBottom: 16,
    opacity: 0.55,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  anonTeaserTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#262322',
  },
  anonTeaserBarTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: '#F0EDE7',
    overflow: 'hidden',
    marginTop: 12,
  },
  anonTeaserBarFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: '#3DAB9E',
  },
  anonTeaserEmpty: {
    fontSize: 12,
    color: '#888280',
    marginTop: 10,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  // Compact "Exit preview" CTA next to the back button -- replaces the
  // full-width preview banner that used to sit below it.
  exitPreviewPill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: '#FACF47',
  },
  exitPreviewText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#4D3F37',
  },
});

// Public per README's "Tabs (pill row)" section: Overview/Games/Clubs are
// public to any logged-in viewer; Training/Assessment are owner-only.
const PRIVATE_TABS: ReadonlySet<string> = new Set(['Training', 'Assessment']);

// All 5 tabs are live as of Milestone 2. Training/Assessment are owner-only
// (a non-owner viewer sees LockedTabPanel instead -- enforced server-side
// too, a non-owner request 403s regardless of what the frontend renders).
const TABS = ['Overview', 'Games', 'Training', 'Assessment', 'Clubs'] as const;

export default function PlayerProfile({navigation, route}: any) {
  const {username} = route?.params ?? {};
  // Owner-only "View as" preview, triggered from ProfileHeader's kebab menu.
  // Declared before the fetch so it feeds the query key.
  const [viewAs, setViewAs] = useState<'self' | ProfileViewAs>('self');
  const previewing = viewAs !== 'self';
  const {data, isLoading, isError} = useGetPlayerProfileApi(
    username,
    previewing ? viewAs : undefined,
  );
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>('Overview');
  // Owner-only privacy toggle. Declared here (before any early return) so hook
  // order stays stable across loading/private/anonymous/full render paths.
  const {mutate: setPrivacy, isLoading: privacyPending} =
    useSetProfilePrivacyApi(username);
  // Owner-only "Personal details" card data -- already-loaded app-wide query,
  // not part of the profile response (see buildPersonalDetails above).
  const dashboardQ = useDashboardApiQuery({});

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.scrollContent}>
          <View style={styles.inner}>
            <View style={styles.skeletonCard}>
              <View style={styles.skeletonAvatar} />
              <View style={{flex: 1}}>
                <View style={[styles.skeletonLine, {width: '45%'}]} />
                <View
                  style={[styles.skeletonLine, {width: '70%', marginTop: 12}]}
                />
              </View>
            </View>
            <CCText style={styles.loadingText}>Loading profile…</CCText>
          </View>
        </View>
      </View>
    );
  }

  if (isError || !data) {
    return (
      <View style={[styles.container, styles.centerState]}>
        <CCText fontWeight="semibold" fontSize={18}>
          Couldn't load this profile.
        </CCText>
        <CCText style={{marginTop: 8, color: '#888280'}}>
          The player may not exist, or something went wrong.
        </CCText>
      </View>
    );
  }

  // Private profile viewed by anyone but the owner (Instagram-style). The
  // backend returns a restricted payload (is_private + identity only); render
  // just the "This profile is private" notice. Checked before the anonymous
  // branch so a private profile never falls through to the teaser.
  const priv = data as PlayerProfilePrivate;
  if (priv.is_private && !priv.is_owner) {
    return (
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          <View style={styles.inner}>
            <View style={styles.backRow}>
              <ProfileBackButton navigation={navigation} />
              {previewing ? (
                <ExitPreviewButton onPress={() => setViewAs('self')} />
              ) : null}
            </View>
            <PrivateProfileNotice profile={priv} />
          </View>
        </ScrollView>
      </View>
    );
  }

  // Anonymous visitor (`login_nudge: true`). Milestone 4: a real teaser UI --
  // identity + rating history + teaser_stats (from AnonymousHeader), an
  // inline nudge banner (not a modal/redirect, per the plan's "Login nudge
  // UX" note), and all 5 tab slots for shape-parity with the logged-in view
  // (Milestone 0's design correction -- the delivered design was missing the
  // Assessment tab). Every tab shows the same locked-teaser content here,
  // since the anonymous payload carries no per-tab data at all.
  if ('login_nudge' in data && data.login_nudge) {
    const goToLogin = () => navigation.navigate('Login');
    return (
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          <View style={styles.inner}>
            {/* No back button on the anonymous page: it's a public teaser a
                logged-out visitor lands on directly, so there's nowhere
                meaningful to go back to. */}
            <AnonymousHeader profile={data} onLogin={goToLogin} />

            {/* Tabs are decorative here: Overview is the (faded) teaser and the
                rest carry a lock glyph -- an anonymous visitor can't open any
                real tab content. */}
            <View style={styles.tabBarRow}>
              <View style={styles.tabStrip}>
                {TABS.map(tab => {
                  const active = tab === 'Overview';
                  return (
                    <Pressable
                      key={tab}
                      testID={`profile-tab-${tab}`}
                      onPress={goToLogin}
                      style={[styles.tabPill, active && styles.tabPillActive]}>
                      <View style={styles.tabPillRow}>
                        <CCText
                          style={[
                            styles.tabPillText,
                            active && styles.tabPillTextActive,
                          ]}>
                          {tab}
                        </CCText>
                        {active ? null : <LockGlyph />}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            {/* Faded preview of the Overview Results card. A single real
                win-rate bar -- the anonymous payload only carries an
                aggregate win_rate, not a win/draw/loss breakdown, so a
                3-segment chart isn't reconstructable (previously this
                rendered 3 bars with hardcoded fake widths). */}
            <View style={styles.anonTeaserCard}>
              <CCText style={styles.anonTeaserTitle}>
                {data.teaser_stats.win_rate != null
                  ? 'Win rate'
                  : 'Results'}{' '}
                · {data.teaser_stats.games} rated games
              </CCText>
              {data.teaser_stats.win_rate != null ? (
                <View style={styles.anonTeaserBarTrack}>
                  <View
                    style={[
                      styles.anonTeaserBarFill,
                      {
                        width: `${Math.round(
                          data.teaser_stats.win_rate * 100,
                        )}%`,
                      },
                    ]}
                  />
                </View>
              ) : (
                <CCText style={styles.anonTeaserEmpty}>
                  Not enough rated games yet.
                </CCText>
              )}
            </View>

            <TeaserNudgeBanner
              name={data.name || data.username}
              onLogin={goToLogin}
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  // From here on, `data` is the full owner/logged-in-visitor payload. `is_owner`
  // is the *only* signal used for context -- never re-derived by comparing
  // usernames client-side (see the plan's explicit anti-pattern warning).
  const profile = data as PlayerProfileFull;
  const isOwner = profile.is_owner;
  const personalDetails = isOwner
    ? buildPersonalDetails((dashboardQ as any).data?.user)
    : null;

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        <View style={styles.inner}>
          <View style={styles.backRow}>
            <ProfileBackButton navigation={navigation} />
            {previewing ? (
              <ExitPreviewButton onPress={() => setViewAs('self')} />
            ) : null}
          </View>

          <FadeInUp>
            <ProfileHeader
              profile={profile}
              isOwner={isOwner}
              navigation={navigation}
              isPrivate={!!profile.is_private}
              privacyPending={privacyPending}
              onTogglePrivacy={() => setPrivacy(!profile.is_private)}
              onViewAsChange={setViewAs}
            />
          </FadeInUp>

          {/* Owner-only, collapsible -- sourced from the dashboard summary
              query (see buildPersonalDetails), never from the profile
              response. isOwner is checked here too so a stale/previewed
              payload can't leak it either. */}
          {isOwner && personalDetails ? (
            <FadeInUp delay={40}>
              <PersonalDetailsCard details={personalDetails} />
            </FadeInUp>
          ) : null}

          <FadeInUp delay={60}>
            <LinkedAccountsRow
              linkedAccounts={profile.linked_accounts}
              isOwner={isOwner}
              navigation={navigation}
            />
          </FadeInUp>

          {/* Tab bar renders all 5 slots for shape-parity with the design's
              locked-tab pattern. All 5 are tappable now -- Training/
              Assessment show LockedTabPanel instead of real content for a
              non-owner viewer, rather than being disabled pills. */}
          <View style={styles.tabBarRow}>
            <View style={styles.tabStrip}>
              {TABS.map(tab => {
                const active = tab === activeTab;
                return (
                  <Pressable
                    key={tab}
                    testID={`profile-tab-${tab}`}
                    onPress={() => setActiveTab(tab)}
                    style={[styles.tabPill, active && styles.tabPillActive]}>
                    <CCText
                      style={[
                        styles.tabPillText,
                        active && styles.tabPillTextActive,
                      ]}>
                      {tab}
                    </CCText>
                  </Pressable>
                );
              })}
            </View>
            <CCText style={styles.tabVisibilityNote}>
              {PRIVATE_TABS.has(activeTab)
                ? isOwner
                  ? `${activeTab} is private — only you can view it`
                  : `${activeTab} is private — you don't have access`
                : `${activeTab} is public — anyone can view it`}
            </CCText>
          </View>

          {/* keyed by activeTab so the panel re-plays its entrance on switch */}
          <FadeInUp key={activeTab} delay={40}>
          {activeTab === 'Games' ? (
            <GamesTab
              username={profile.username ?? undefined}
              navigation={navigation}
              isOwner={isOwner}
            />
          ) : activeTab === 'Training' ? (
            isOwner ? (
              <TrainingTab username={profile.username ?? undefined} />
            ) : (
              <LockedTabPanel ownerName={profile.name} />
            )
          ) : activeTab === 'Assessment' ? (
            isOwner ? (
              <AssessmentTab
                username={profile.username ?? undefined}
                navigation={navigation}
              />
            ) : (
              <LockedTabPanel ownerName={profile.name} />
            )
          ) : activeTab === 'Clubs' ? (
            <ClubsTab username={profile.username ?? undefined} navigation={navigation} />
          ) : (
            <OverviewTab
              profile={profile}
              navigation={navigation}
              onSeeAllGames={() => setActiveTab('Games')}
              isOwner={isOwner}
            />
          )}
          </FadeInUp>
        </View>
      </ScrollView>
    </View>
  );
}
